// @flow
import zentakeApi from 'api'
import useResource from 'components/ResourceFill/hooks/useResource'
import { ResourceNavigationContext } from 'components/ResourceFill/providers/ResourceNavigationProvider'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import type { SubmitContext } from 'components/ResourceFill/types/submit-context'
import useQuery from 'hooks/useQuery'
import * as React from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { Session } from 'types/session'
import type { SessionObject } from 'types/session-object'
import { ZentakeContext } from 'utils/ZentakeContext'

type Props = {
  children: React.Node,
}

export const ResourceSubmitContext: React.Context<?SubmitContext> =
  React.createContext(null)

function ResourceSubmitProvider({ children }: Props): React.Node {
  const history = useHistory()
  const query = useQuery()
  const isDone = query.get('done')
  const { url } = useRouteMatch()

  const {
    isPacket,
    redirectOnSubmitted,
    mode,
    isTablet,
    formIndex,
    afterSubmit,
    // source,
  } = React.useContext(ResourceSharedContext) || {}

  const { currentSession, setCurrentSession, sessions, setSessions } =
    React.useContext(ResourceSessionsContext) || {}

  const { response } = React.useContext(ResourceResponseContext) || {}

  const { onNextForm, updateFormIndex } =
    React.useContext(ResourceNavigationContext) || {}

  const resource = useResource()

  const { setAlertConfig, setResponseSubmitted } =
    React.useContext(ZentakeContext)

  const [submitting, setSubmitting] = React.useState(false)

  const message = React.useMemo(
    () =>
      isPacket ? currentSession?.packetResponse?.message : response?.message,
    [isPacket, currentSession?.packetResponse?.message, response?.message]
  )

  const shouldRedirectToScheduler = React.useMemo(() => {
    if (!resource) return false

    return Boolean(
      resource.settings?.require_appointment &&
        !resource.settings?.schedule_before_submit
    )
  }, [resource])

  const doRedirect = React.useCallback(() => {
    if (!redirectOnSubmitted) return

    setResponseSubmitted(true)

    if (shouldRedirectToScheduler) {
      history.push(`/s/${currentSession?.messageCode ?? ''}/`)

      return
    }

    history.push(
      `/r/${currentSession?.messageCode ?? ''}/submitted${
        history?.location?.search ?? ''
      }`
    )
  }, [
    currentSession?.messageCode,
    history,
    redirectOnSubmitted,
    setResponseSubmitted,
    shouldRedirectToScheduler,
  ])

  const submitClient = React.useCallback(() => {
    if (mode !== 'client') return

    doRedirect()
  }, [doRedirect, mode])

  const isWaiting = React.useMemo(
    () =>
      isPacket
        ? currentSession?.packetResponse?.message?.is_waiting || false
        : response?.message?.is_waiting === true,
    [
      currentSession?.packetResponse?.message?.is_waiting,
      isPacket,
      response?.message?.is_waiting,
    ]
  )

  const getOfficeUseSubmitConfirmAlertTitle = React.useCallback(
    (willEnqueue) => {
      if (willEnqueue) {
        return 'Are you sure you want to add this to the queue?'
      }

      if (isWaiting) {
        return 'Are you sure you want to send?'
      }

      return 'Are you sure you want to save?'
    },
    [isWaiting]
  )

  const getSubmitConfirmAlertTitle = React.useCallback(
    (willEnqueue, lockInternalForm) => {
      if (mode === 'office') {
        return getOfficeUseSubmitConfirmAlertTitle(willEnqueue)
      }

      if (lockInternalForm) {
        return 'Are you sure you want to save and lock this form?'
      }

      if (mode === 'internal') {
        return 'Do you want to save your progress?'
      }

      return 'Are you sure you want to submit?'
    },
    [getOfficeUseSubmitConfirmAlertTitle, mode]
  )

  const getOfficeUseSubmitConfirmAlertButtonLabel = React.useCallback(
    (willEnqueue) => {
      if (willEnqueue) {
        return 'Yes'
      }

      if (isWaiting) {
        return 'Send'
      }

      return 'Save'
    },
    [isWaiting]
  )

  const getSubmitConfirmAlertButtonLabel = React.useCallback(
    (willEnqueue, lockInternalForm) => {
      if (mode === 'office') {
        return getOfficeUseSubmitConfirmAlertButtonLabel(willEnqueue)
      }

      if (lockInternalForm) {
        return 'Lock'
      }

      if (mode === 'internal') {
        return 'Save'
      }

      return 'Submit'
    },
    [getOfficeUseSubmitConfirmAlertButtonLabel, mode]
  )

  const getSubmitConfirmAlertDescription = React.useCallback(
    (willEnqueue, lockInternalForm) => {
      if (mode === 'office') {
        return ''
      }

      if (lockInternalForm) {
        return 'Once locked, no further edits can be made.'
      }

      if (mode === 'internal') {
        return 'You can return and make changes later if needed.'
      }

      return 'After submitting you will not be able to make any additional changes.'
    },
    [mode]
  )

  const submitConfirmAlert = React.useCallback(
    (willEnqueue, lockInternalForm) =>
      new Promise((resolve) =>
        setAlertConfig({
          title: getSubmitConfirmAlertTitle(willEnqueue, lockInternalForm),
          description: getSubmitConfirmAlertDescription(
            willEnqueue,
            lockInternalForm
          ),
          buttons: [
            {
              title: getSubmitConfirmAlertButtonLabel(
                willEnqueue,
                lockInternalForm
              ),
              style: 'primary',
              onPress: () => resolve(true),
              dataCy: 'confirm-submit-button',
            },
            {
              title: willEnqueue ? 'No' : 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [
      getSubmitConfirmAlertButtonLabel,
      getSubmitConfirmAlertDescription,
      getSubmitConfirmAlertTitle,
      setAlertConfig,
    ]
  )

  const navigateToClient = React.useCallback(() => {
    const baseUrl = url.split('/')
    const pushUrl = [...baseUrl.slice(0, -1), 'client'].join('/')

    history.push(pushUrl)
  }, [history, url])

  const submitTabletOfficeUse = React.useCallback(
    (willEnqueue: boolean) => {
      if (mode !== 'office') return
      if (!isTablet) return

      if (willEnqueue) {
        navigateToClient()
      }

      navigateToClient()
    },
    [isTablet, mode, navigateToClient]
  )

  const getRedirectUrlRoot = React.useCallback(() => {
    if (isDone) {
      return 'response'
    }

    return 'response'
  }, [isDone])

  const getRedirectUrlSlug = React.useCallback(() => {
    if (isPacket) {
      return currentSession?.packetResponse?.id ?? ''
    }

    return response?.id ?? ''
  }, [currentSession?.packetResponse?.id, isPacket, response?.id])

  const getQueryParams = React.useCallback(
    () => `response_type=${isPacket ? 'packet' : 'form'}`,
    [isPacket]
  )

  const getRedirectUrl = React.useCallback(() => {
    if (isWaiting) return '/'

    const root = getRedirectUrlRoot()
    const slug = getRedirectUrlSlug()
    const queryParams = getQueryParams()

    return `/${root}/${slug}?${queryParams}`
  }, [getQueryParams, getRedirectUrlRoot, getRedirectUrlSlug, isWaiting])

  const submitInternal = React.useCallback(() => {
    if (mode !== 'internal') return

    history.push('/')
    toast.success('Internal Form Complete')
  }, [history, mode])

  const sendResourceToClient = React.useCallback(async () => {
    if (!response) return

    if (!message) return

    await zentakeApi.updateMessage(message.id, { is_waiting: false })
  }, [message, response])

  const showOfficeToast = React.useCallback(() => {
    if (!message) return

    if (isWaiting) {
      toast.info(`${isPacket ? 'Packet' : 'Form'} Sent`)
      return
    }

    toast.success('Saved')
  }, [isPacket, isWaiting, message])

  const submitOfficeUse = React.useCallback(
    async (willEnqueue) => {
      if (!response) return
      if (mode !== 'office') return
      if (isTablet) return
      if (!message) return

      if (!willEnqueue && isWaiting) {
        await sendResourceToClient()
      }

      window.scrollTo({ top: 0, behavior: 'instant' })

      if (!willEnqueue) {
        setSubmitting(false)
        history.push(getRedirectUrl())
        showOfficeToast()
      }
    },
    [
      getRedirectUrl,
      history,
      isTablet,
      isWaiting,
      message,
      mode,
      response,
      sendResourceToClient,
      showOfficeToast,
    ]
  )

  const submit = React.useCallback(
    async ({ willEnqueue = false, lockInternalForm = false }) => {
      if (submitting) return
      if (!currentSession) return
      if (!onNextForm) return
      if (!updateFormIndex) return
      if (!sessions) return
      if (!setCurrentSession) return
      if (!setSessions) return

      try {
        const willProceed = await submitConfirmAlert(
          willEnqueue,
          lockInternalForm
        )

        if (!willProceed) return false

        setSubmitting(true)

        const updatedSession = await onNextForm({
          submit: mode !== 'office',
          lockInternalForm,
        })

        if (!updatedSession) {
          setSubmitting(false)
          return
        }

        updateFormIndex(formIndex || 0)

        const newCurrentSession: Session = {
          ...updatedSession,
          isSubmitted: mode !== 'office',
          isWaiting: mode === 'office' ? false : updatedSession.isWaiting,
          responses: updatedSession?.responses?.map((r) => ({
            ...r,
            is_submitted: mode !== 'office',
          })),
        }

        setCurrentSession(newCurrentSession)

        const key = newCurrentSession?.id ?? ''

        const newFillSessions: SessionObject = {
          ...sessions,
          [key]: newCurrentSession,
        }

        setSessions(newFillSessions)

        if (afterSubmit) {
          afterSubmit()
        }

        submitInternal()
        submitClient()
        submitTabletOfficeUse(willEnqueue)
        await submitOfficeUse(willEnqueue)
        if (!willEnqueue) {
          setSubmitting(false)
        }
        return true
      } catch (error) {
        setSubmitting(false)
      }
    },
    [
      afterSubmit,
      currentSession,
      formIndex,
      mode,
      onNextForm,
      sessions,
      setCurrentSession,
      setSessions,
      submitClient,
      submitConfirmAlert,
      submitInternal,
      submitOfficeUse,
      submitTabletOfficeUse,
      submitting,
      updateFormIndex,
    ]
  )

  const value = React.useMemo(
    () => ({ submit, submitting, isWaiting }),
    [isWaiting, submit, submitting]
  )

  return (
    <ResourceSubmitContext.Provider value={value}>
      {children}
    </ResourceSubmitContext.Provider>
  )
}

export default ResourceSubmitProvider
